import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api';

const formatTime = (isoString) => new Date(isoString).toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit' });

// Shared modal used by both SveskaView and CalendarView to show everyone
// booked into one slot (single patient or a group), and to cancel/move them.
// Stays mounted permanently (parents render it unconditionally, it just
// returns null when there's nothing to show) - so `appointments` must be
// re-synced into local state via an effect, not a useState initializer,
// otherwise localAppointments freezes at whatever it was on first mount.
const AppointmentGroupModal = ({ appointments, onClose, onRefresh }) => {
    const navigate = useNavigate();
    const [localAppointments, setLocalAppointments] = useState(appointments || []);
    const [doctors, setDoctors] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({ date: '', startTime: '', doctorId: '' });
    const [editServiceId, setEditServiceId] = useState(null);
    const [editSlots, setEditSlots] = useState([]);
    const [confirmingCancelId, setConfirmingCancelId] = useState(null);

    useEffect(() => {
        API.get('/doctors').then(res => setDoctors(res.data)).catch(() => {});
    }, []);

    useEffect(() => {
        setLocalAppointments(appointments || []);
        setEditingId(null);
        setConfirmingCancelId(null);
        setEditForm({ date: '', startTime: '', doctorId: '' });
        setEditServiceId(null);
    }, [appointments]);

    // Slobodni termini za izabranog doktora/datum/uslugu dok se termin menja - uključuje
    // i trenutno zakazano vreme (iako ga sopstveni termin "zauzima") da se ne izgubi iz padajuće liste.
    useEffect(() => {
        if (!editingId || !editForm.doctorId || !editForm.date || !editServiceId) {
            setEditSlots([]);
            return;
        }
        API.get('/appointments/free-slots', {
            params: { doctorId: editForm.doctorId, date: editForm.date, serviceId: editServiceId }
        })
            .then(res => setEditSlots(res.data.map(t => t.substring(0, 5))))
            .catch(() => setEditSlots([]));
    }, [editingId, editForm.doctorId, editForm.date, editServiceId]);

    if (!appointments) return null;

    const editInGroupScheduling = () => {
        const a = localAppointments[0];
        if (!a) return;
        const qs = new URLSearchParams({
            doctorId: a.doctorId,
            serviceId: a.serviceId,
            date: a.startTime.split('T')[0],
            startTime: a.startTime.split('T')[1].substring(0, 5)
        });
        onClose();
        navigate(`/group-scheduling?${qs.toString()}`);
    };

    const startEdit = (a) => {
        setEditingId(a.id);
        setConfirmingCancelId(null);
        setEditForm({
            date: a.startTime.split('T')[0],
            startTime: a.startTime.split('T')[1].substring(0, 5),
            doctorId: a.doctorId || ''
        });
        setEditServiceId(a.serviceId || null);
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditForm({ date: '', startTime: '', doctorId: '' });
        setEditServiceId(null);
        setEditSlots([]);
    };

    const confirmEdit = async (id) => {
        if (!editForm.date || !editForm.startTime || !editForm.doctorId) {
            alert("Izaberite datum, vreme i doktora.");
            return;
        }
        try {
            await API.put(`/appointments/${id}/reschedule`, {
                date: editForm.date,
                startTime: editForm.startTime,
                doctorId: editForm.doctorId
            });
            setLocalAppointments(prev => prev.filter(a => a.id !== id));
            cancelEdit();
            onRefresh();
        } catch (err) {
            alert(err.response?.data?.message || "Greška pri izmeni termina.");
        }
    };

    const doCancel = async (id) => {
        try {
            await API.put(`/appointments/${id}/cancel`);
            setLocalAppointments(prev => prev.filter(a => a.id !== id));
            setConfirmingCancelId(null);
            onRefresh();
        } catch (err) {
            alert("Greška pri otkazivanju termina.");
        }
    };

    return (
        <div style={styles.modalBackdrop} onClick={onClose}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h3 style={styles.modalTitle}>
                        {localAppointments[0]?.serviceName}
                        {localAppointments[0] ? ` — ${formatTime(localAppointments[0].startTime)}` : ''}
                    </h3>
                    <button style={styles.closeBtn} onClick={onClose}>✕</button>
                </div>
                {localAppointments.length > 0 && (
                    <div style={styles.editBar}>
                        <button style={styles.editBtn} onClick={editInGroupScheduling}>
                            Izmeni u grupnim terminima
                        </button>
                    </div>
                )}
                <div style={styles.modalList}>
                    {localAppointments.length === 0 && (
                        <div style={{ padding: '15px', color: '#94a3b8' }}>Nema pacijenata u ovom terminu.</div>
                    )}
                    {localAppointments.map(a => (
                        <div key={a.id} style={styles.modalRow}>
                            {editingId === a.id ? (
                                <div style={styles.formBlock}>
                                    <div style={styles.pName}>{a.patientFullName}</div>
                                    <select
                                        style={styles.moveInput}
                                        value={editForm.doctorId}
                                        onChange={(e) => setEditForm({ ...editForm, doctorId: e.target.value })}
                                    >
                                        <option value="">Izaberi doktora...</option>
                                        {doctors.map(d => <option key={d.id} value={d.id}>{d.firstName} {d.lastName}</option>)}
                                    </select>
                                    <div style={styles.moveInputs}>
                                        <input
                                            type="date"
                                            style={styles.moveInput}
                                            value={editForm.date}
                                            onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                                        />
                                        <select
                                            style={styles.moveInput}
                                            value={editForm.startTime}
                                            onChange={(e) => setEditForm({ ...editForm, startTime: e.target.value })}
                                        >
                                            <option value="">Izaberi termin...</option>
                                            {editForm.startTime && !editSlots.includes(editForm.startTime) && (
                                                <option value={editForm.startTime}>{editForm.startTime} (trenutni)</option>
                                            )}
                                            {editSlots.map(t => <option key={t} value={t}>{t}</option>)}
                                        </select>
                                    </div>
                                    <div style={styles.formActions}>
                                        <button style={styles.confirmBtn} onClick={() => confirmEdit(a.id)}>Sačuvaj</button>
                                        <button style={styles.backBtn} onClick={cancelEdit}>Nazad</button>
                                    </div>
                                </div>
                            ) : confirmingCancelId === a.id ? (
                                <div style={styles.formBlock}>
                                    <div style={styles.pName}>Otkazati termin za {a.patientFullName}?</div>
                                    <div style={styles.formActions}>
                                        <button style={styles.removeBtn} onClick={() => doCancel(a.id)}>Da, otkaži termin</button>
                                        <button style={styles.backBtn} onClick={() => setConfirmingCancelId(null)}>Nazad</button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div>
                                        <div style={styles.pName}>{a.patientFullName}</div>
                                        <div style={styles.pSub}>{a.patientPhone}</div>
                                    </div>
                                    <div style={styles.rowActions}>
                                        <button style={styles.moveBtn} onClick={() => startEdit(a)}>Izmeni</button>
                                        <button style={styles.removeBtn} onClick={() => { setConfirmingCancelId(a.id); setEditingId(null); }}>Otkaži</button>
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const styles = {
    modalBackdrop: {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.5)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '15px'
    },
    modal: {
        backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '450px',
        maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
    },
    modalHeader: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '15px', borderBottom: '1px solid #f1f5f9'
    },
    modalTitle: { margin: 0, fontSize: '16px', color: '#1e293b' },
    closeBtn: {
        background: 'none', border: 'none', fontSize: '16px', cursor: 'pointer',
        color: '#94a3b8', padding: '4px 8px'
    },
    editBar: { padding: '10px 15px', borderBottom: '1px solid #f1f5f9' },
    editBtn: { width: '100%', padding: '8px', backgroundColor: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
    modalList: { overflowY: 'auto' },
    modalRow: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '12px 15px', borderBottom: '1px solid #f8fafc'
    },
    pName: { fontSize: '15px', fontWeight: '600', color: '#1e293b', textTransform: 'capitalize' },
    pSub: { fontSize: '12px', color: '#64748b' },
    rowActions: { display: 'flex', gap: '8px' },
    moveBtn: { padding: '6px 12px', backgroundColor: '#f59e0b', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
    removeBtn: { padding: '6px 12px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
    formBlock: { width: '100%', display: 'flex', flexDirection: 'column', gap: '8px' },
    moveInputs: { display: 'flex', gap: '8px' },
    moveInput: { flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' },
    formActions: { display: 'flex', gap: '8px' },
    confirmBtn: { padding: '6px 12px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
    backBtn: { padding: '6px 12px', backgroundColor: '#94a3b8', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }
};

export default AppointmentGroupModal;
